import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { 
    Phase, 
    TreeNode, 
    AnswerRecord, 
    AnswerValue, 
    AppView,
    ReportItem,
    ShoppingListItem
} from '../types';

// Examples cache: questionId -> example text
interface ExamplesMap {
    [questionId: string]: string;
}

// Snapshot saved before each answer so we can undo navigation
interface HistorySnapshot {
    currentNode: TreeNode | null;
    pathStack: number[];
    answers: AnswerRecord[];
    cartIds: string[];
    userCartCount: number;
}

// State interface
interface AssessmentState {
    // Setup
    phase: Phase | null;
    area: string;
    sessionId: string | null;
    
    // Tree & traversal
    tree: TreeNode[];
    currentNode: TreeNode | null;
    pathStack: number[];  // Track position: [rootIdx, childIdx, grandchildIdx, ...]
    
    // Examples cache
    examples: ExamplesMap;
    
    // Answers & cart
    answers: AnswerRecord[];
    cartIds: string[];           // All cart items (user-selected + descendants) for final list
    userCartCount: number;       // Count of user-selected items only (for badge)
    
    // UI state
    view: AppView;
    isLoading: boolean;
    error: string | null;
    
    // Navigation history for back button
    history: HistorySnapshot[];

    // Results (fetched at end)
    report: ReportItem[];
    shoppingList: ShoppingListItem[];

    // Participants
    participantOneName: string;
    participantTwoName: string;
}

// Initial state
const initialState: AssessmentState = {
    phase: null,
    area: '',
    sessionId: null,
    tree: [],
    currentNode: null,
    pathStack: [],
    examples: {},
    answers: [],
    cartIds: [],
    userCartCount: 0,
    history: [],
    view: 'landing',
    isLoading: false,
    error: null,
    report: [],
    shoppingList: [],
    participantOneName: '',
    participantTwoName: ''
};

// Action types
type Action =
    | { type: 'SET_PHASE'; payload: Phase }
    | { type: 'SET_AREA'; payload: string }
    | { type: 'START_SESSION'; payload: { sessionId: string; tree: TreeNode[]; examples: ExamplesMap } }
    | { type: 'SET_CURRENT_NODE'; payload: { node: TreeNode | null; pathStack: number[] } }
    | { type: 'RECORD_ANSWER'; payload: AnswerRecord }
    | { type: 'ADD_TO_CART'; payload: string[] }
    | { type: 'SET_VIEW'; payload: AppView }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_ERROR'; payload: string | null }
    | { type: 'SET_RESULTS'; payload: { report: ReportItem[]; shoppingList: ShoppingListItem[] } }
    | { type: 'SET_PARTICIPANTS'; payload: { participantOneName: string; participantTwoName: string } }
    | { type: 'PUSH_HISTORY' }
    | { type: 'GO_BACK' }
    | { type: 'RESET' };

// Reducer
function assessmentReducer(state: AssessmentState, action: Action): AssessmentState {
    switch (action.type) {
        case 'SET_PHASE':
            return { ...state, phase: action.payload };
        case 'SET_AREA':
            return { ...state, area: action.payload };
        case 'START_SESSION':
            return {
                ...state,
                sessionId: action.payload.sessionId,
                tree: action.payload.tree,
                examples: action.payload.examples,
                currentNode: action.payload.tree[0] || null,
                pathStack: action.payload.tree.length > 0 ? [0] : [],
                view: 'intro',
                answers: [],
                cartIds: [],
                userCartCount: 0,
                history: [],
            };
        case 'SET_CURRENT_NODE':
            return {
                ...state,
                currentNode: action.payload.node,
                pathStack: action.payload.pathStack
            };
        case 'RECORD_ANSWER':
            return {
                ...state,
                answers: [...state.answers, action.payload]
            };
        case 'ADD_TO_CART':
            // First item is the user-selected node, rest are descendants
            const newCartIds = [...state.cartIds];
            action.payload.forEach(id => {
                if (!newCartIds.includes(id)) {
                    newCartIds.push(id);
                }
            });
            return { 
                ...state, 
                cartIds: newCartIds,
                userCartCount: state.userCartCount + 1  // Only count user selection
            };
        case 'SET_VIEW':
            return { ...state, view: action.payload };
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };
        case 'SET_ERROR':
            return { ...state, error: action.payload };
        case 'SET_RESULTS':
            return {
                ...state,
                report: action.payload.report,
                shoppingList: action.payload.shoppingList,
                view: 'results'
            };
        case 'SET_PARTICIPANTS':
            return {
                ...state,
                participantOneName: action.payload.participantOneName,
                participantTwoName: action.payload.participantTwoName
            };
        case 'PUSH_HISTORY':
            return {
                ...state,
                history: [
                    ...state.history,
                    {
                        currentNode: state.currentNode,
                        pathStack: state.pathStack,
                        answers: state.answers,
                        cartIds: state.cartIds,
                        userCartCount: state.userCartCount,
                    },
                ],
            };
        case 'GO_BACK': {
            if (state.history.length === 0) return state;
            const snapshot = state.history[state.history.length - 1];
            return {
                ...state,
                currentNode: snapshot.currentNode,
                pathStack: snapshot.pathStack,
                answers: snapshot.answers,
                cartIds: snapshot.cartIds,
                userCartCount: snapshot.userCartCount,
                history: state.history.slice(0, -1),
            };
        }
        case 'RESET':
            return {
                ...initialState,
                view: 'names'
            };
        default:
            return state;
    }
}

// Context
interface AssessmentContextType {
    state: AssessmentState;
    dispatch: React.Dispatch<Action>;
}

const AssessmentContext = createContext<AssessmentContextType | undefined>(undefined);

// Provider
export const AssessmentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(assessmentReducer, initialState);

    return (
        <AssessmentContext.Provider value={{ state, dispatch }}>
            {children}
        </AssessmentContext.Provider>
    );
};

// Hook
export const useAssessment = () => {
    const context = useContext(AssessmentContext);
    if (context === undefined) {
        throw new Error('useAssessment must be used within an AssessmentProvider');
    }
    return context;
};

export { AssessmentContext };